import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaArrowRight, FaCheck, FaCheckCircle, FaExclamationTriangle, FaFileUpload, FaInfoCircle, FaPen, FaRedo } from 'react-icons/fa';
import { AnaliseContrato, aprenderContratoService, mensagemDeErro, onboardingService, TAMANHO_MAXIMO_ARQUIVO, VARIAVEL_NOME_LOCATARIO } from '../../services/onboarding';
import { rastrearTeste, segundosDesde } from '../../services/rastreamentoTeste';
import {
    Aviso, Botao, CampoNome, Carregando, Chip, Chips, Destaque, FaixaSucesso, Rodape, Rotulo, RotuloComIcone, Secao, Subtitulo, Titulo, ZonaArquivo,
} from './styles';

interface Props {
    /** Sem modelo: seguiu sem contrato (onboarding) ou cancelou (cadastro). */
    aoConcluir: (idModeloContrato?: string) => void;
    /** "cadastro": a mesma tela dentro de Modelos de Contrato, fora do onboarding. */
    contexto?: 'onboarding' | 'cadastro';
    /** No cadastro: abrir o modelo recém-criado no editor, para ajustar o texto e o que a IA não marcou. */
    aoPersonalizar?: (idModeloContrato: string) => void;
    /** No cadastro: a leitura automática não deu certo; quem usa segue pelo cadastro normal e avisa o motivo. */
    aoFalhar?: (motivo: string) => void;
}

// Motivo curto de cada falha da leitura automática, para o aviso de quem segue pelo cadastro normal.
const MOTIVO_FALHA: Partial<Record<ResultadoLeitura, string>> = {
    falha_leitura: 'Não conseguimos ler o contrato agora.',
    erro: 'Não conseguimos ler o contrato agora.',
    sem_campos: 'Não identificamos os dados do cliente e do veículo neste contrato.',
    sem_nome: 'Não achamos onde vai o nome do cliente neste contrato.',
};

// O que muda entre o onboarding (primeiro contrato, dá para seguir sem) e o cadastro de modelos.
const TEXTOS = {
    onboarding: {
        titulo: 'Envie um contrato que você já fez com um cliente',
        sair: 'Seguir sem contrato',
        depois: 'siga sem contrato agora e configure o seu depois, em Cadastros, Contrato.',
        ajusteTitulo: 'Isto é só uma demonstração',
        ajuste: 'Algo errado ou faltando? Você ajusta 100% do contrato depois, em Cadastros, Contrato.',
        salvoTexto: 'Ele já vai sair preenchido na sua primeira locação. Antes de seguir, confira:',
        salvoAjuste: 'Você ajusta 100% do contrato em Cadastros, Contrato.',
        salvar: 'Salvar e continuar',
        concluir: 'Continuar',
        erroSalvar: 'Não foi possível salvar o contrato agora. Tente de novo em instantes ou siga sem contrato.',
    },
    cadastro: {
        titulo: 'Criar modelo a partir de um contrato pronto',
        sair: 'Cancelar',
        depois: 'crie o modelo do zero pelo botão Novo.',
        ajusteTitulo: 'Confira antes de usar',
        ajuste: 'Salve o modelo e, se faltar algum dado ou quiser mudar o texto, personalize em seguida.',
        salvoTexto: 'Ele já pode ser escolhido nas locações.',
        salvoAjuste: 'Faltou algum dado ou quer mudar o texto? Em Personalizar contrato você edita o documento e insere os campos que quiser.',
        salvar: 'Salvar modelo',
        concluir: 'Concluir',
        erroSalvar: 'Não foi possível salvar o modelo agora. Tente de novo em instantes.',
    },
};

type Fase = 'escolher' | 'analisando' | 'falhou' | 'pronto' | 'salvando' | 'salvo';

const TIPO_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const ehPdf = (nome: string) => nome.toLowerCase().endsWith('.pdf');

// Formatos de editor de texto que não lemos: o Word (e os outros editores) salvam em .docx.
const OUTROS_EDITORES = /\.(doc|odt|pages|rtf|wps|txt)$/i;

/** Motivo para recusar o arquivo antes de enviar, ou vazio quando ele serve. */
const problemaDoArquivo = (arquivo: File): string => {
    const nome = arquivo.name.toLowerCase();
    if (OUTROS_EDITORES.test(nome)) return 'Ainda não lemos este formato. Salve o contrato como .docx no Word (Arquivo, Salvar como) e envie de novo.';
    if (!nome.endsWith('.docx') && !ehPdf(nome)) return 'Envie o contrato em Word (.docx) ou PDF.';
    if (arquivo.size > TAMANHO_MAXIMO_ARQUIVO) return 'Este arquivo passa de 10 MB. Envie um arquivo menor, de preferência o contrato em Word (.docx).';
    if (arquivo.size === 0) return 'Este arquivo está vazio. Confira e envie de novo.';
    return '';
};

type ResultadoLeitura = 'aprendido' | 'sem_nome' | 'sem_campos' | 'falha_leitura' | 'erro' | 'cancelado' | 'arquivo_recusado';

const tipoDoArquivo = (nome: string) => (nome.toLowerCase().endsWith('.docx') ? 'docx' : ehPdf(nome) ? 'pdf' : 'outro');

const MENSAGEM_FALHA_LEITURA = 'Não conseguimos ler o contrato agora. Tente de novo em instantes.';

const arquivoDeBase64 = (base64: string, nome: string) => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    return new File([bytes], nome, { type: TIPO_DOCX });
};

const nomeSugerido = (arquivo: string) => {
    const semExtensao = arquivo.replace(/\.(docx|pdf)$/i, '').replace(/[_-]+/g, ' ').trim();
    return semExtensao ? semExtensao.charAt(0).toUpperCase() + semExtensao.slice(1) : 'Meu contrato';
};

export default function EtapaContrato({ aoConcluir, contexto = 'onboarding', aoPersonalizar, aoFalhar }: Props) {
    const t = TEXTOS[contexto];
    const servico = contexto === 'cadastro'
        ? aprenderContratoService
        : { analisar: onboardingService.analisarContrato, salvar: onboardingService.salvarContrato };
    // Os eventos do cadastro ficam separados dos do onboarding na análise dos testantes.
    const evento = (nome: string) => `${contexto === 'cadastro' ? 'modelo' : 'onboarding'}_contrato_${nome}`;
    // No cadastro, falha da leitura automática não prende ninguém aqui: segue pelo cadastro normal.
    const seguirPeloCadastroNormal = (resultado: ResultadoLeitura) => {
        const motivo = MOTIVO_FALHA[resultado];
        if (contexto !== 'cadastro' || !aoFalhar || !motivo) return false;
        aoFalhar(motivo);
        return true;
    };
    const [fase, setFase] = useState<Fase>('escolher');
    // O arquivo como a pessoa mandou (para tentar de novo) e o que será salvo (o PDF vira Word).
    const [original, setOriginal] = useState<File | null>(null);
    const [arquivo, setArquivo] = useState<File | null>(null);
    const [analise, setAnalise] = useState<AnaliseContrato | null>(null);
    const [nome, setNome] = useState('');
    const [arrastando, setArrastando] = useState(false);
    const [passoLeitura, setPassoLeitura] = useState(0);
    const [erroArquivo, setErroArquivo] = useState('');
    const [erroSalvar, setErroSalvar] = useState('');
    const [falhaLeitura, setFalhaLeitura] = useState('');
    const [avisosSalvar, setAvisosSalvar] = useState<string[]>([]);
    const [idSalvo, setIdSalvo] = useState<string | undefined>();
    const leitura = useRef<AbortController | null>(null);
    // Início da leitura em andamento, para medir quanto ela levou.
    const inicioLeitura = useRef(0);

    const registrarResultado = (resultado: ResultadoLeitura, analisado?: AnaliseContrato | null) => {
        const marcacoesLidas = analisado?.marcacoes ?? [];
        rastrearTeste(evento('resultado'), {
            resultado,
            campos: marcacoesLidas.length,
            segundos: inicioLeitura.current ? segundosDesde(inicioLeitura.current) : 0,
            convertidoDePdf: Boolean(analisado?.convertidoDePdf),
            qtdAvisos: (analisado?.avisos ?? []).length,
        });
    };

    useEffect(() => {
        if (fase !== 'analisando') return;
        setPassoLeitura(0);
        const id = window.setTimeout(() => setPassoLeitura(1), 2500);
        return () => window.clearTimeout(id);
    }, [fase]);

    // Saindo da etapa no meio da leitura, a requisição não fica pendurada.
    useEffect(() => () => leitura.current?.abort(), []);

    const analisar = async (escolhido: File | undefined | null) => {
        if (!escolhido) return;
        rastrearTeste(evento('enviado'), { tipo: tipoDoArquivo(escolhido.name), tamanhoKb: Math.round(escolhido.size / 1024) });
        inicioLeitura.current = Date.now();
        const problema = problemaDoArquivo(escolhido);
        if (problema) {
            registrarResultado('arquivo_recusado');
            setErroArquivo(problema);
            return;
        }
        setErroArquivo('');
        setErroSalvar('');
        setFalhaLeitura('');
        setOriginal(escolhido);
        setArquivo(escolhido);
        setAnalise(null);
        setNome(nomeSugerido(escolhido.name));
        setFase('analisando');
        const controle = new AbortController();
        leitura.current?.abort();
        leitura.current = controle;
        try {
            const resultado = await servico.analisar(escolhido, controle.signal);
            if (leitura.current !== controle) return;
            if (resultado.falhaNaLeitura) {
                registrarResultado('falha_leitura', resultado);
                if (seguirPeloCadastroNormal('falha_leitura')) return;
                setFalhaLeitura(MENSAGEM_FALHA_LEITURA);
                setFase('falhou');
                return;
            }
            if (resultado.arquivoConvertido) {
                setArquivo(arquivoDeBase64(resultado.arquivoConvertido, escolhido.name.replace(/\.pdf$/i, '.docx')));
            }
            const lidas = resultado.marcacoes ?? [];
            const desfecho: ResultadoLeitura = lidas.length === 0 ? 'sem_campos' : !lidas.some(m => m.variavel === VARIAVEL_NOME_LOCATARIO) ? 'sem_nome' : 'aprendido';
            registrarResultado(desfecho, resultado);
            if (seguirPeloCadastroNormal(desfecho)) return;
            setAnalise(resultado);
            setFase('pronto');
        } catch (error: any) {
            if (leitura.current !== controle || axios.isCancel(error) || controle.signal.aborted) return;
            const status = error?.response?.status;
            if (!error?.response || status >= 500) {
                // Problema do nosso lado ou da conexão: o arquivo não tem culpa.
                registrarResultado('erro');
                if (seguirPeloCadastroNormal('erro')) return;
                setFalhaLeitura(MENSAGEM_FALHA_LEITURA);
                setFase('falhou');
                return;
            }
            registrarResultado('arquivo_recusado');
            setErroArquivo(mensagemDeErro(error, 'Não conseguimos ler este arquivo. Confira se ele abre no seu computador e envie de novo.'));
            setFase('escolher');
        } finally {
            if (leitura.current === controle) leitura.current = null;
        }
    };

    const cancelarLeitura = () => {
        registrarResultado('cancelado');
        leitura.current?.abort();
        leitura.current = null;
        recomecar();
    };

    const seguirSemContrato = () => {
        const momento = fase === 'escolher' ? 'inicio' : fase === 'analisando' ? 'analisando' : fase === 'falhou' || erroSalvar ? 'erro' : 'resultado';
        rastrearTeste(evento('pulado'), { momento });
        leitura.current?.abort();
        leitura.current = null;
        aoConcluir(undefined);
    };

    const salvar = async () => {
        if (!arquivo || !analise) return;
        setFase('salvando');
        setErroSalvar('');
        try {
            const salvo = await servico.salvar(arquivo, analise.marcacoes, nome);
            rastrearTeste(evento('salvo'), { campos: analise.marcacoes.length, qtdAvisos: salvo.avisos.length });
            // No cadastro a tela final sempre aparece: dela a pessoa conclui ou vai personalizar o modelo.
            if (salvo.avisos.length > 0 || contexto === 'cadastro') {
                // Algo não saiu perfeito (ex.: dado antigo que não deu para trocar): mostra antes de seguir.
                setIdSalvo(salvo.idModelo);
                setAvisosSalvar(salvo.avisos);
                setFase('salvo');
                return;
            }
            aoConcluir(salvo.idModelo);
        } catch (error) {
            if (contexto === 'cadastro' && aoFalhar) {
                aoFalhar(mensagemDeErro(error, 'Não foi possível criar o modelo a partir do arquivo.'));
                return;
            }
            setErroSalvar(mensagemDeErro(error, t.erroSalvar));
            setFase('pronto');
        }
    };

    function recomecar() {
        setOriginal(null);
        setArquivo(null);
        setAnalise(null);
        setFalhaLeitura('');
        setErroSalvar('');
        setFase('escolher');
    }

    const rodapeSemContrato = (
        <Rodape>
            <Botao type="button" $variante="link" onClick={recomecar}>Enviar outro arquivo</Botao>
            <Botao type="button" onClick={seguirSemContrato}>{t.sair} {contexto === 'onboarding' && <FaArrowRight />}</Botao>
        </Rodape>
    );

    if (fase === 'falhou') {
        return (
            <>
                <Titulo>Não conseguimos ler o contrato agora</Titulo>
                <Subtitulo>{falhaLeitura || MENSAGEM_FALHA_LEITURA} O problema não é o seu arquivo.</Subtitulo>
                <Aviso><FaInfoCircle /> Se preferir, {t.depois}</Aviso>
                <Rodape>
                    <Botao type="button" $variante="link" onClick={seguirSemContrato}>{t.sair}</Botao>
                    <Botao type="button" onClick={() => analisar(original)} disabled={!original}><FaRedo /> Tentar de novo</Botao>
                </Rodape>
            </>
        );
    }

    const marcacoes = analise?.marcacoes ?? [];
    const camposIdentificados = Array.from(new Set(marcacoes.map(m => m.descricao || m.variavel)));

    if (fase === 'salvo') {
        return (
            <>
                <FaixaSucesso>
                    <FaCheckCircle />
                    <div>
                        <h1>{contexto === 'cadastro' ? 'Modelo criado' : 'Contrato salvo'}</h1>
                        <p>{t.salvoTexto}{avisosSalvar.length > 0 ? ' Antes de seguir, confira:' : ''}</p>
                    </div>
                </FaixaSucesso>
                <ListaAvisos avisos={avisosSalvar} />
                {contexto === 'cadastro' && camposIdentificados.length > 0 && (
                    <Secao>
                        <RotuloComIcone><FaCheckCircle />A IA identificou e vai preencher em cada locação</RotuloComIcone>
                        <Chips>{camposIdentificados.map(c => <Chip key={c}>{c}</Chip>)}</Chips>
                    </Secao>
                )}
                <Destaque style={contexto === 'cadastro' ? { marginTop: 14 } : undefined}>
                    <FaInfoCircle />
                    <div>
                        <strong>{contexto === 'cadastro' ? 'Quer ajustar alguma coisa?' : 'Dá para ajustar depois'}</strong>
                        <span>{t.salvoAjuste}</span>
                    </div>
                </Destaque>
                <Rodape>
                    {contexto === 'cadastro' && aoPersonalizar && idSalvo
                        ? <Botao type="button" $variante="secundario" onClick={() => aoPersonalizar(idSalvo)}><FaPen /> Personalizar contrato</Botao>
                        : <span />}
                    <Botao type="button" onClick={() => aoConcluir(idSalvo)}>{t.concluir} {contexto === 'onboarding' ? <FaArrowRight /> : <FaCheck />}</Botao>
                </Rodape>
            </>
        );
    }

    const avisosAnalise = [
        ...(analise?.avisos ?? []),
        ...(analise?.convertidoDePdf && !(analise?.avisos ?? []).length ? ['O seu PDF virou um Word simples: a formatação pode ficar diferente do original.'] : []),
    ];
    if (fase === 'pronto' || fase === 'salvando') {
        if (marcacoes.length === 0) {
            return (
                <>
                    <Titulo>Não conseguimos aprender este contrato</Titulo>
                    <Subtitulo>O arquivo não tem espaços para os dados do cliente ou do veículo que conseguimos identificar.</Subtitulo>
                    <Aviso><FaInfoCircle /> Sem problema: tente outro arquivo ou {t.depois}</Aviso>
                    {rodapeSemContrato}
                </>
            );
        }

        // Sem o nome do cliente o contrato não serve de modelo: cada locação sairia com o nome errado.
        if (!marcacoes.some(m => m.variavel === VARIAVEL_NOME_LOCATARIO)) {
            return (
                <>
                    <Titulo>Não achamos onde vai o nome do cliente</Titulo>
                    <Subtitulo>Para cada locação sair com o contrato certo, precisamos saber onde o nome do cliente aparece. Neste arquivo não conseguimos encontrar.</Subtitulo>
                    <Aviso><FaInfoCircle /> Tente outro contrato em que o nome do cliente esteja escrito, de preferência em Word (.docx). Ou {t.depois}</Aviso>
                    {rodapeSemContrato}
                </>
            );
        }

        return (
            <>
                <FaixaSucesso>
                    <FaCheckCircle />
                    <div>
                        <h1>Aprendemos o seu contrato</h1>
                        <p>Em cada nova locação, ele sai preenchido com os dados do cliente e do veículo.</p>
                    </div>
                </FaixaSucesso>

                <ListaAvisos avisos={avisosAnalise} />

                <Destaque>
                    <FaInfoCircle />
                    <div>
                        <strong>{t.ajusteTitulo}</strong>
                        <span>{t.ajuste}</span>
                    </div>
                </Destaque>

                <Rotulo htmlFor="nome-modelo">Como quer chamar este contrato?</Rotulo>
                <CampoNome id="nome-modelo" value={nome} maxLength={100} onChange={e => setNome(e.target.value)} placeholder="Ex.: Contrato de locação de motos" />

                <Secao>
                    <RotuloComIcone><FaCheckCircle />Preenchido automaticamente em cada locação</RotuloComIcone>
                    <Chips>{camposIdentificados.map(c => <Chip key={c}>{c}</Chip>)}</Chips>
                </Secao>

                {erroSalvar && <Aviso $tipo="erro" style={{ marginTop: 14 }}><FaExclamationTriangle /> {erroSalvar}</Aviso>}

                <Rodape>
                    <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <Botao type="button" $variante="link" onClick={recomecar} disabled={fase === 'salvando'}>Enviar outro arquivo</Botao>
                        {(erroSalvar || contexto === 'cadastro') && <Botao type="button" $variante="link" onClick={seguirSemContrato} disabled={fase === 'salvando'}>{t.sair}</Botao>}
                    </span>
                    <Botao type="button" onClick={salvar} disabled={fase === 'salvando' || !nome.trim()}>
                        {fase === 'salvando' ? 'Salvando...' : t.salvar} <FaArrowRight />
                    </Botao>
                </Rodape>
            </>
        );
    }

    return (
        <>
            <Titulo>{t.titulo}</Titulo>
            <Subtitulo>Não precisa ser em branco: pode mandar um contrato já preenchido, em Word ou PDF. O sistema identifica os dados do cliente e do veículo e transforma o seu contrato em modelo.</Subtitulo>

            <Aviso style={{ marginBottom: 14 }}><FaInfoCircle /> Se tiver o contrato em Word (.docx), prefira ele: o PDF vira um Word simples.</Aviso>

            <ZonaArquivo
                $arrastando={arrastando}
                onDragOver={e => { e.preventDefault(); setArrastando(true); }}
                onDragLeave={() => setArrastando(false)}
                onDrop={e => { e.preventDefault(); setArrastando(false); if (fase === 'escolher') analisar(e.dataTransfer.files?.[0]); }}
            >
                {fase === 'analisando' ? (
                    <>
                        <Carregando />
                        <strong>{passoLeitura === 0 ? 'Lendo o seu contrato...' : 'Identificando os dados do cliente e do veículo...'}</strong>
                        <span>Em Word costuma levar alguns segundos. PDF escaneado pode levar até um ou dois minutos.</span>
                    </>
                ) : (
                    <>
                        <FaFileUpload />
                        <strong>Arraste o arquivo aqui ou clique para escolher</strong>
                        <span>Word (.docx) ou PDF, até 10 MB</span>
                    </>
                )}
                <input type="file" accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" disabled={fase === 'analisando'}
                    onChange={e => { analisar(e.target.files?.[0]); e.target.value = ''; }} />
            </ZonaArquivo>

            {erroArquivo && <Aviso $tipo="erro" style={{ marginTop: 14 }}><FaExclamationTriangle /> {erroArquivo}</Aviso>}

            <Rodape>
                <Botao type="button" $variante="link" onClick={seguirSemContrato}>{t.sair}</Botao>
                {fase === 'analisando'
                    ? <Botao type="button" $variante="secundario" onClick={cancelarLeitura}>Cancelar</Botao>
                    : <span />}
            </Rodape>
        </>
    );
}

// Avisos da leitura e do salvar (ex.: PDF que virou Word simples, dado antigo que não deu para
// trocar): visíveis, mas sem cara de erro.
function ListaAvisos({ avisos }: { avisos: string[] }) {
    if (!avisos.length) return null;
    return (
        <Aviso style={{ marginBottom: 14 }}>
            <FaInfoCircle />
            {avisos.length === 1
                ? <span>{avisos[0]}</span>
                : <ul style={{ margin: 0, paddingLeft: 18 }}>{avisos.map(a => <li key={a}>{a}</li>)}</ul>}
        </Aviso>
    );
}
